import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CONVERT_TARGETS } from "@/data/notebookOptions";
import { OWNERS, PRIORITY_LEVELS } from "@/data/commandOptions";

// Field sets per target: what the preview form exposes for editing before confirmation.
const FIELDS = {
  Task: [["title", "Title", "text"], ["description", "Description", "textarea"], ["assigned_to", "Assigned to", OWNERS], ["priority", "Priority", PRIORITY_LEVELS]],
  Priority: [["title", "Title", "text"], ["description", "Description", "textarea"], ["owner", "Owner", OWNERS], ["priority", "Priority", PRIORITY_LEVELS]],
  Blocker: [["title", "Title", "text"], ["description", "Description", "textarea"], ["owner", "Owner", OWNERS], ["severity", "Severity", PRIORITY_LEVELS]],
  Decision: [["title", "Title", "text"], ["context", "Context", "textarea"], ["owner", "Owner", OWNERS], ["priority", "Priority", PRIORITY_LEVELS]],
  "Future Innovation": [["title", "Title", "text"], ["description", "Description", "textarea"], ["owner", "Owner", OWNERS], ["priority", "Priority", PRIORITY_LEVELS]],
  "Company Update": [["title", "Title", "text"], ["body", "Update text", "textarea"]],
  "Weekly Review highlight": [["title", "Title", "text"], ["body", "Highlight", "textarea"]],
};

const seed = (target, note) => {
  const base = { title: note.title, description: note.body, body: note.body, context: note.body,
    owner: note.author === "Richard" ? "Richard" : "Steven", assigned_to: note.author === "Richard" ? "Richard" : "Steven",
    priority: "Medium", severity: "Medium" };
  const out = {};
  (FIELDS[target] || []).forEach(([k]) => { out[k] = base[k] ?? ""; });
  return out;
};

export const ConvertDialog = ({ open, onOpenChange, note, onConverted }) => {
  const [target, setTarget] = useState("Task");
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && note) { setTarget("Task"); setData(seed("Task", note)); } }, [open, note]);
  const changeTarget = (t) => { setTarget(t); setData(seed(t, note)); };
  const set = (k, val) => setData((s) => ({ ...s, [k]: val }));

  const confirm = async () => {
    if (!(data.title || "").trim()) { toast.error("A title is required"); return; }
    setBusy(true);
    try {
      const result = await onConverted(target, data);
      if (result) onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Conversion failed");
    } finally { setBusy(false); }
  };

  if (!note) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="convert-dialog" className="max-h-[92vh] max-w-lg overflow-y-auto border-[#E7E2D4] bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl text-[#0D1B2A]"><ArrowRightLeft size={18} className="text-[#E0A82E]" /> Convert Note</DialogTitle>
          <DialogDescription className="text-[#8A8674]">Preview and edit the new record. The original note is kept and marked <strong>Converted</strong>.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8A8674]">Convert to</Label>
            <Select value={target} onValueChange={changeTarget}><SelectTrigger data-testid="convert-target" className="border-[#E7E2D4]"><SelectValue /></SelectTrigger><SelectContent>{CONVERT_TARGETS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="rounded-xl border border-[#E7E2D4] bg-[#FBF6E9] p-3">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[#8A6314]">Preview — {target}</p>
            <div className="space-y-3" data-testid="convert-preview">
              {(FIELDS[target] || []).map(([k, label, kind]) => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs font-semibold text-[#5C6B7B]">{label}</Label>
                  {Array.isArray(kind) ? (
                    <Select value={data[k] || undefined} onValueChange={(x) => set(k, x)}><SelectTrigger data-testid={`convert-field-${k}`} className="border-[#E7E2D4] bg-white"><SelectValue /></SelectTrigger><SelectContent>{kind.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
                  ) : kind === "textarea" ? (
                    <Textarea data-testid={`convert-field-${k}`} value={data[k] || ""} onChange={(e) => set(k, e.target.value)} rows={3} className="border-[#E7E2D4] bg-white" />
                  ) : (
                    <Input data-testid={`convert-field-${k}`} value={data[k] || ""} onChange={(e) => set(k, e.target.value)} className="border-[#E7E2D4] bg-white" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E7E2D4]">Cancel</Button>
          <Button data-testid="convert-confirm" onClick={confirm} disabled={busy} className="bg-[#E0A82E] text-white hover:bg-[#C4901F]">Confirm & Create {target}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
